import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { t } from '../i18n';
import { useCaseData } from '../hooks';
import { api, ApiError } from '../api';
import { Feld, Fehler } from '../components/ui';
import type { Me } from '../App';
import { Step1Fall } from './steps/Step1Fall';
import { Step2Vertraege } from './steps/Step2Vertraege';
import { Step3Soll } from './steps/Step3Soll';
import { Step4Ist } from './steps/Step4Ist';
import { Step5Auswirkungen } from './steps/Step5Auswirkungen';
import { Step6Nachbesserung } from './steps/Step6Nachbesserung';
import { Step7Bewertungsauftrag } from './steps/Step7Bewertungsauftrag';
import { Step8Berechnung } from './steps/Step8Berechnung';
import { Step9Sensitivitaet } from './steps/Step9Sensitivitaet';
import { Step10Abschluss } from './steps/Step10Abschluss';

export interface StepProps {
  me: Me;
  caseId: string;
  data: any;
  reload: () => void;
  readonly: boolean;
}

const STEPS = [
  { nr: 1, key: 'FALL' },
  { nr: 2, key: 'VERTRAEGE' },
  { nr: 3, key: 'SOLL' },
  { nr: 4, key: 'IST' },
  { nr: 5, key: 'AUSWIRKUNGEN' },
  { nr: 6, key: 'NACHBESSERUNG' },
  { nr: 7, key: 'BEWERTUNGSAUFTRAG' },
  { nr: 8, key: 'BERECHNUNG' },
  { nr: 9, key: 'SENSITIVITAET' },
  { nr: 10, key: 'ABSCHLUSS' },
] as const;

export function CasePage({ me }: { me: Me }) {
  const { caseId, step } = useParams<{ caseId: string; step: string }>();
  const stepNr = Math.min(Math.max(Number(step) || 1, 1), 10);
  const { data, error, loading, reload } = useCaseData(caseId);
  const navigate = useNavigate();

  if (loading) return <p>{t('app.loading')}</p>;
  if (error || !data || !caseId) return <div className="fehlermeldung">{t('error.notFound')}</div>;

  const readonly = me.role === 'LESER' || me.organization.licenseExpired;
  const props: StepProps = { me, caseId, data, reload, readonly };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.8rem', flexWrap: 'wrap' }}>
        <h1 style={{ marginBottom: 0 }}>
          {data.caseNumber} — {data.title}
        </h1>
        {data.isDemo && <span className="badge warn">DEMO</span>}
        <Link to={`/faelle/${caseId}/audit`} style={{ marginLeft: 'auto' }}>
          {t('audit.link')}
        </Link>
      </div>
      {data.isDemo && <p className="demo-banner">{t('app.demo.banner')}</p>}

      <nav aria-label={t('steps.navLabel')}>
        <ol className="stepper">
          {STEPS.map((s) => (
            <li key={s.nr}>
              <Link
                to={`/faelle/${caseId}/schritt/${s.nr}`}
                className={s.nr === stepNr ? 'aktiv' : s.nr < stepNr ? 'besucht' : ''}
                aria-current={s.nr === stepNr ? 'step' : undefined}
              >
                <span className="nr">{s.nr}</span>
                {t(`steps.${s.key}`)}
              </Link>
            </li>
          ))}
        </ol>
      </nav>

      {stepNr === 1 && <Step1Fall {...props} />}
      {stepNr === 2 && <Step2Vertraege {...props} />}
      {stepNr === 3 && <Step3Soll {...props} />}
      {stepNr === 4 && <Step4Ist {...props} />}
      {stepNr === 5 && <Step5Auswirkungen {...props} />}
      {stepNr === 6 && <Step6Nachbesserung {...props} />}
      {stepNr === 7 && <Step7Bewertungsauftrag {...props} />}
      {stepNr === 8 && <Step8Berechnung {...props} />}
      {stepNr === 9 && <Step9Sensitivitaet {...props} />}
      {stepNr === 10 && <Step10Abschluss {...props} />}

      <div className="aktionen" style={{ marginTop: '2rem' }}>
        {stepNr > 1 && (
          <button
            type="button"
            className="knopf-sekundaer"
            onClick={() => navigate(`/faelle/${caseId}/schritt/${stepNr - 1}`)}
          >
            {t('common.back')}
          </button>
        )}
        {stepNr < 10 && (
          <button
            type="button"
            className="knopf-primaer"
            onClick={() => navigate(`/faelle/${caseId}/schritt/${stepNr + 1}`)}
          >
            {t('steps.next', { step: t(`steps.${STEPS[stepNr]?.key ?? 'ABSCHLUSS'}`) })}
          </button>
        )}
      </div>

      <FallVerwaltung me={me} caseId={caseId} data={data} reload={reload} readonly={readonly} />
    </>
  );
}

/**
 * Fallverwaltung (Auftrag 12): Archivieren durch Gutachter; endgültige Löschung
 * nur durch Admin mit Zwei-Schritt-Bestätigung (exakte Fallnummer). Der
 * Audit-Eintrag bleibt bestehen.
 */
function FallVerwaltung({
  me,
  caseId,
  data,
  reload,
  readonly,
}: {
  me: Me;
  caseId: string;
  data: any;
  reload: () => void;
  readonly: boolean;
}) {
  const [confirmNumber, setConfirmNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const canArchive = !readonly && (me.role === 'GUTACHTER' || me.role === 'ADMIN');
  const canDelete = !readonly && me.role === 'ADMIN';
  if (!canArchive && !canDelete) return null;

  async function archive() {
    setError(null);
    try {
      await api.post(`/api/v1/cases/${caseId}/archive`);
      reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'INTERNAL');
    }
  }

  async function permanentDelete() {
    setError(null);
    try {
      await api.del(`/api/v1/cases/${caseId}/permanent`, { confirmCaseNumber: confirmNumber });
      navigate('/', { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'INTERNAL');
    }
  }

  return (
    <details className="karte" style={{ marginTop: '2.5rem' }}>
      <summary style={{ cursor: 'pointer', color: 'var(--text-schwach)' }}>
        {t('caseAdmin.title')}
      </summary>
      {canArchive && (
        <div style={{ marginTop: '0.8rem' }}>
          <p className="erklaerung">{t('caseAdmin.archiveIntro')}</p>
          <button
            type="button"
            className="knopf-sekundaer"
            onClick={archive}
            disabled={data.status === 'ARCHIVIERT'}
          >
            {data.status === 'ARCHIVIERT' ? t('caseAdmin.archived') : t('caseAdmin.archiveAction')}
          </button>
        </div>
      )}
      {canDelete && (
        <div style={{ marginTop: '1.2rem' }}>
          <p className="erklaerung">{t('caseAdmin.deleteIntro')}</p>
          <Feld
            label={t('caseAdmin.deleteConfirmLabel', { caseNumber: data.caseNumber })}
            hint={t('caseAdmin.deleteHint')}
          >
            <input value={confirmNumber} onChange={(e) => setConfirmNumber(e.target.value)} />
          </Feld>
          <button
            type="button"
            className="knopf-gefahr"
            onClick={permanentDelete}
            disabled={confirmNumber !== data.caseNumber}
          >
            {t('caseAdmin.deleteAction')}
          </button>
        </div>
      )}
      <Fehler code={error} />
    </details>
  );
}
